import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { useHistory, navigate } from "raviger";
import * as api from "../../../../backend/request";
import { hasUserAccess, getUserRole } from "../../../../backend/auth";
import { toast } from "react-toastify";
import PageLoader from "../../../shared/PageLoader";
import { useGetAllActivities, useExportTodos } from "../../../../backend/query";
import { useQueryClient } from "@tanstack/react-query";
import { useAuditReport } from "../../../../backend/exports";
import { download, downloadWithPresignedUrl, downloadFileContent, preventDefault } from "../../../../utils/common";
import { ACTIVITY_STATUS, AUDIT_STATUS, STATUS_MAPPING, TOOLTIP_DELAY } from "../../../common/Constants";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { ACTIVITY_TYPE, ERROR_MESSAGES } from "../../../../utils/constants";
import { ActivityType } from "../../Masters/Master.constants";
import { DEFAULT_PAGE_SIZE } from "../../../common/Table";
import {
  AUDIT_STATUS_LABELS,
  AUDIT_STATUS as LOCATION_AUDIT_STATUS,
  canSubmitToAuditor, getNewAuditStatusAfterSubmit,
  USER_ROLE,
} from "../../../../utils/auditStateMachine";
import { USER_PRIVILEGES } from "../../UserManagement/Roles/RoleConfiguration";
import { AxiosError } from "axios";
import SubmitToAuditorModalV2 from "./SubmitToAuditorModalV2";
import EditActivityModalV2 from "./EditActivityModalV2";
import BulkUploadModal from "./BulkuploadModal";
import VendorBulkUploadModal from "./VendorToDoBulkModalV2";
import AlertModalV2 from "../../../common/AlertModalV2";
import AuditDetailsModalV2 from "./AuditDetailsModalV2";
import AuditLocationFiltersV2 from "../../../common/AuditLocationFiltersV2";
import DatePicker from "react-datepicker";
import Select from "react-select";
import AuditSelect from '../../../common/AuditSelect';
import "../../../common/ActivitiesManagementV2.css";

const INCLUDE_FIELDS = ["act", "rule", "activity", "location", "associate_company", "company"];
const AUDIT_INCLUDE = ["company", "associate_company", "location", "preliminary_report", "final_report"];
const PER = 25;


function getActivityTypeIcon(auditted: string) {
  const clipboardBase = (
    <>
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
      <rect x="9" y="3" width="6" height="4" rx="1"/>
    </>
  );
  if (auditted === ACTIVITY_TYPE.PHYSICAL_AUDIT) {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E5A000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
        {clipboardBase}
        <circle cx="13" cy="14" r="2.5"/>
        <line x1="14.8" y1="15.8" x2="16.5" y2="17.5"/>
      </svg>
    );
  }
  if (auditted === ACTIVITY_TYPE.NO_AUDIT) {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#757575" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
        {clipboardBase}
        <line x1="9" y1="14" x2="15" y2="14"/>
      </svg>
    );
  }
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#14ae5c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
      {clipboardBase}
      <path d="M9 14l2 2 4-4"/>
    </svg>
  );
}

const STATUS_BTNS = [
  { name: ACTIVITY_STATUS.ACTIVITY_SAVED, label: STATUS_MAPPING[ACTIVITY_STATUS.ACTIVITY_SAVED] },
  { name: ACTIVITY_STATUS.PENDING,        label: STATUS_MAPPING[ACTIVITY_STATUS.PENDING] },
  { name: ACTIVITY_STATUS.OVERDUE,        label: STATUS_MAPPING[ACTIVITY_STATUS.OVERDUE] },
  { name: ACTIVITY_STATUS.SUBMITTED,      label: STATUS_MAPPING[ACTIVITY_STATUS.SUBMITTED] },
  { name: ACTIVITY_STATUS.REJECTED,       label: STATUS_MAPPING[ACTIVITY_STATUS.REJECTED] },
  { name: ACTIVITY_STATUS.AUDITED,        label: STATUS_MAPPING[ACTIVITY_STATUS.AUDITED] },
];

function getAuditStatusPillClass(status: string): string {
  if (!status) return "";
  if (([LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_PENDING] as string[]).includes(status))
    return "al-audit-pill-scheduled";
  if (([
    LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_SUBMITTED,
    LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_REPORT_UPLOADING,
    LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_REPORT_FAILED,
  ] as string[]).includes(status))
    return "al-audit-pill-inprogress";
  if (([
    LOCATION_AUDIT_STATUS.FINAL_AUDIT_PENDING,
    LOCATION_AUDIT_STATUS.FINAL_AUDIT_EDITING,
    LOCATION_AUDIT_STATUS.FINAL_AUDIT_SUBMITTED,
    LOCATION_AUDIT_STATUS.FINAL_AUDIT_REPORT_UPLOADING,
    LOCATION_AUDIT_STATUS.FINAL_AUDIT_REPORT_FAILED,
  ] as string[]).includes(status))
    return "al-audit-pill-preliminary";
  if (([
    LOCATION_AUDIT_STATUS.FINAL_AUDIT_PUBLISHED,
    LOCATION_AUDIT_STATUS.FINAL_AUDIT_REPORT_UPLOADED,
  ] as string[]).includes(status))
    return "al-audit-pill-final";
  if (([LOCATION_AUDIT_STATUS.DROPPED_AUDIT] as string[]).includes(status))
    return "al-audit-pill-dropped";
  return "";
}

function getPageNumbers(current: number, last: number): number[] {
  const delta = 2;
  const start = Math.max(1, current - delta);
  const end = Math.min(last, current + delta);
  const range: number[] = [];
  for (let i = start; i <= end; i++) range.push(i);
  return range;
}

function InfoTooltip({ html }: { html: string }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLSpanElement>(null);
  if (!html) return null;

  function handleMouseEnter() {
    if (ref && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPos({ top: rect.top, left: rect.left + rect.width / 2 });
    }
    setShow(true);
  }

  return (
    <span
      ref={ref}
      className="al-info-tooltip-wrap"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShow(false)}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
      {show && (
        <div
          className="al-info-tooltip-box"
          style={{ position: 'fixed', top: pos.top, left: pos.left, transform: 'translateX(-50%) translateY(calc(-100% - 6px))' }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}
    </span>
  );
}

function TextCell({ className, value }: { className: string; value: string }) {
  const text = value || "—";
  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{text}</Tooltip>}
      delay={{ show: TOOLTIP_DELAY } as any}
    >
      <td className={className}>{text}</td>
    </OverlayTrigger>
  );
}

function ActivitiesManagementV2({ auditId }: any) {
  const { state }: any = useHistory();
  const queryClient = useQueryClient();

  const [currentAudit, setCurrentAudit] = useState<any>({});
  const [submitting, setSubmitting] = useState(false);
  const [userRole] = useState(getUserRole());

  // Inline filter dropdowns
  const [inlineDueDate, setInlineDueDate] = useState<Date | null>(null);
  const [inlineActivityType, setInlineActivityType] = useState<string[]>([]);
  const [inlineAuditType, setInlineAuditType] = useState("");

  // Status toggle buttons
  const [checkedStatuses, setCheckedStatuses] = useState<any>({});

  // More filters drawer
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [appliedMoreFilters, setAppliedMoreFilters] = useState<any>({});

  // Pagination
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(PER);
  const [payload, setPayload] = useState<any>(null);

  // Bulk upload
  const [bulkUpload, setBulkUpload] = useState(false);
  const [vendorUpload, setVendorUpload] = useState(false);

  // Checkbox row selection (for Bulk Upload)
  const [rowCheckState, setRowCheckState] = useState<Record<string, boolean>>({});

  // Modal state
  const [activity, setActivity] = useState<any>(null);
  const [submitToAuditor, setSubmitToAuditor] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [alertMessage, setAlertMessage] = useState<any>(null);
  const [showAuditDetails, setShowAuditDetails] = useState(false);
  const [showEllipsis, setShowEllipsis] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [selectedReportForDownload, setSelectedReportForDownload] = useState<any>(null);

  // Refs for click-outside detection on dropdown menus
  const downloadWrapRef = useRef<any>(null);
  const ellipsisWrapRef = useRef<any>(null);

  // Mutable refs to avoid stale closures in payload builder
  const currentAuditRef = useRef<any>({});
  currentAuditRef.current = currentAudit;
  const inlineFiltersRef = useRef<any>({});
  const statusFiltersRef = useRef<any>({});
  const moreFiltersRef = useRef<any>({});
  moreFiltersRef.current = appliedMoreFilters;

  // DOM refs for scroll sync and row-height sync
  const tableScrollRef = useRef<any>(null);
  const stickyBodyRef = useRef<any>(null);
  const stickyBodyInnerRef = useRef<any>(null);
  const tbodyRef = useRef<any>(null);

  // ─── Data hooks ───────────────────────────────────────────────────────────

  const { activities, total, isFetching, refetch } = useGetAllActivities(
    payload,
    !!(payload && payload.filters && payload.filters.location_audit_id_eq)
  );

  const { auditReport, exporting } = useAuditReport(
    (response: any) => {
      if (response) {
        const filename =
          (selectedReportForDownload && selectedReportForDownload.name)
            ? selectedReportForDownload.name.charAt(0).toUpperCase() + selectedReportForDownload.name.slice(1)
            : "audit_report";
        downloadFileContent({ name: filename, type: response.headers["content-type"], content: response.data });
      }
    },
    () => toast.error(ERROR_MESSAGES.DEFAULT)
  );

  const { exportTodos, exporting: exportingTodos } = useExportTodos(
    (response: any) => {
      downloadFileContent({ name: "Audit Activities.xlsx", type: response.headers["content-type"], content: response.data });
    },
    () => toast.error(ERROR_MESSAGES.DEFAULT)
  );

  // ─── Payload builder + reset ──────────────────────────────────────────────

  function buildPayload(pageNum: number, per?: number) {
    const audit = currentAuditRef.current || {};
    const inlineFs = inlineFiltersRef.current || {};
    const statusFs = statusFiltersRef.current || {};
    const moreFs = moreFiltersRef.current || {};
    const apiMoreFs: any = {};
    if (moreFs.act_id_eq) apiMoreFs.act_id_eq = moreFs.act_id_eq;
    if (moreFs.activity_id_eq) apiMoreFs.activity_id_eq = moreFs.activity_id_eq;
    if (moreFs.rule_id_eq) apiMoreFs.rule_id_eq = moreFs.rule_id_eq;
    return {
      page: pageNum, per: per !== undefined ? per : perPage, sorts: ["month desc"],
      include: INCLUDE_FIELDS, includes: INCLUDE_FIELDS,
      filters: { location_audit_id_eq: audit.id, ...inlineFs, ...statusFs, ...apiMoreFs },
    };
  }

  function resetAndFetch(pageNum = 1, per?: number) {
    const audit = currentAuditRef.current || {};
    if (!(audit && audit.id)) return;
    setPage(pageNum);
    setPayload(buildPayload(pageNum, per));
  }

  function handlePerPageChange(newPer: number) {
    setPerPage(newPer);
    resetAndFetch(1, newPer);
  }

  // ─── useEffects ───────────────────────────────────────────────────────────

  useEffect(() => {
    if (auditId) { refetchAudit(); } else { navigate("/location-audits"); }
  }, [auditId]);

  useEffect(() => {
    if (currentAudit && currentAudit.id) resetAndFetch(1);
  }, [currentAudit]);

  // Inline filter changes → rebuild payload
  useEffect(() => {
    const fs: any = {};
    if (inlineDueDate) fs.due_date_lteq = dayjs(inlineDueDate).endOf("day").toISOString();
    if (inlineActivityType.length > 0) fs.activity_activity_type_in = inlineActivityType;
    if (inlineAuditType) fs.auditted_in = inlineAuditType;
    inlineFiltersRef.current = fs;
    resetAndFetch(1);
  }, [inlineDueDate, inlineActivityType, inlineAuditType]);

  // Status toggle → rebuild payload
  useEffect(() => {
    const active = Object.keys(checkedStatuses).filter((k) => !!checkedStatuses[k]);
    statusFiltersRef.current = active.length > 0 ? { status_in: active } : {};
    resetAndFetch(1);
  }, [checkedStatuses]);

  // Scroll sync: sticky panel body drives vertical scroll; wheel over table syncs to it
  useEffect(() => {
    const tableEl = tableScrollRef.current;
    const stickyEl = stickyBodyRef.current;
    if (!tableEl || !stickyEl) return;
    function onStickyScroll() {
      tableEl.scrollTop = stickyEl.scrollTop;
    }
    function onTableWheel(e: WheelEvent) {
      const deltaY = (e as any).deltaY;
      const deltaX = (e as any).deltaX;
      if (deltaY !== 0 && Math.abs(deltaY) >= Math.abs(deltaX)) {
        stickyEl.scrollTop += deltaY;
        e.preventDefault();
      }
    }
    stickyEl.addEventListener("scroll", onStickyScroll);
    tableEl.addEventListener("wheel", onTableWheel, { passive: false });
    return () => {
      stickyEl.removeEventListener("scroll", onStickyScroll);
      tableEl.removeEventListener("wheel", onTableWheel);
    };
  }, []);

  // Row-height sync: match sticky panel row heights to table row heights after each data change
  useEffect(() => {
    const tbody = tbodyRef.current;
    const stickyInner = stickyBodyInnerRef.current;
    const stickyBody = stickyBodyRef.current;
    if (!tbody || !stickyInner) return;
    if (stickyBody) stickyBody.scrollTop = 0;
    if (tableScrollRef.current) tableScrollRef.current.scrollTop = 0;
    const rafId = requestAnimationFrame(() => {
      const trs = tbody.querySelectorAll("tr");
      const stickyRows = stickyInner.querySelectorAll(".al-sticky-row");
      for (let i = 0; i < trs.length; i++) {
        if (stickyRows[i]) {
          stickyRows[i].style.height = trs[i].getBoundingClientRect().height + "px";
        }
      }
    });
    return () => cancelAnimationFrame(rafId);
  }, [activities]);

  // Close dropdown menus when clicking outside their containers
  useEffect(() => {
    function handleDocumentClick(e: MouseEvent) {
      if (downloadWrapRef.current && !downloadWrapRef.current.contains(e.target as Node)) {
        setShowDownloadMenu(false);
      }
      if (ellipsisWrapRef.current && !ellipsisWrapRef.current.contains(e.target as Node)) {
        setShowEllipsis(false);
      }
    }
    document.addEventListener("click", handleDocumentClick);
    return () => document.removeEventListener("click", handleDocumentClick);
  }, []);

  // ─── API helpers ──────────────────────────────────────────────────────────

  async function refetchAudit() {
    if (auditId) {
      try {
        const response = await api.get(`/api/v1/location_audits/${auditId}`, {
          include: AUDIT_INCLUDE,
          includes: AUDIT_INCLUDE,
        });
        if (response && response.data) {
          setCurrentAudit(response.data);
        }
      } catch (error) {
        console.error("Error refreshing audit:", error);
      }
    }
  }

  function handleMoreFiltersApply(filters: Record<string, any>) {
    moreFiltersRef.current = filters;
    setAppliedMoreFilters(filters);
    resetAndFetch(1);
  }

  function getMoreFiltersCount() {
    let n = 0;
    if (appliedMoreFilters.act_id_eq) n++;
    if (appliedMoreFilters.activity_id_eq) n++;
    if (appliedMoreFilters.rule_id_eq) n++;
    return n;
  }

  function handlePageNav(newPage: number) {
    resetAndFetch(newPage);
  }

  function onSubmitToAuditor() {
    if (!canSubmitToAuditor(currentAudit, userRole)) {
      toast.error("You cannot submit activities to auditor at this stage.");
    } else {
      setSubmitting(true);
      const _payload: any = {
        filters: {
          location_audit_id_eq: currentAudit.id,
        },
      };
      api.get("/api/v1/todos", _payload).then((response) => {
        if (response && response.data) {
          const _rows = ((response.data || { data: {} }).items) || [];
          const _applicableRows = _rows.filter(
            (x: any) => ![ACTIVITY_STATUS.AUDITED, ACTIVITY_STATUS.SUBMITTED].includes(x.status)
          );
          const _published = _applicableRows.filter((x: any) => x.published);
          if (_published.length > 0) {
            setAlertMessage(
              "All the activities are published for the selected month and year. See Audit report for more details."
            );
          } else if (_applicableRows.length) {
            setSelectedRows(_applicableRows);
            setSubmitToAuditor(true);
          } else if ([LOCATION_AUDIT_STATUS.FINAL_AUDIT_PENDING].includes(currentAudit.status)) {
            setSelectedRows(_applicableRows);
            setSubmitToAuditor(true);
          } else {
            toast.warn("There are no activities available for submission.");
          }
        }
      }).finally(() => setSubmitting(false));
    }
  }

  async function submitToAuditorPreliminaryAudit(todoIds: any[]) {
    setSubmitting(true);
    try {
      const newAuditStatus = LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_SUBMITTED;
      const updatePayload = { todo_ids: todoIds };
      const response: any = await api.put(
        `/api/v1/location_audits/${currentAudit.id}/${newAuditStatus}`,
        updatePayload
      );
      if (response instanceof AxiosError) {
        toast.error("Failed to submit preliminary audit. Please try again.");
      } else {
        setTimeout(() => {
          toast.success("Activities submitted to auditor successfully.");
          refetch();
          refetchAudit();
          setSubmitToAuditor(false);
          setSubmitting(false);
        }, 2000);
      }
    } catch (error) {
      toast.error("Failed to submit activities. Please try again.");
      console.error("Error submitting activities:", error);
      setSubmitToAuditor(false);
      setSubmitting(false);
    }
  }

  async function submitToAuditorFinalAudit(todoIds: any[]) {
    setSubmitting(true);
    try {
      const newAuditStatus = LOCATION_AUDIT_STATUS.FINAL_AUDIT_SUBMITTED;
      const updatePayload = { todo_ids: todoIds };
      const response: any = await api.put(
        `/api/v1/location_audits/${currentAudit.id}/${newAuditStatus}`,
        updatePayload
      );
      if (response instanceof AxiosError) {
        toast.error("Failed to submit final audit. Please try again.");
      } else {
        setTimeout(() => {
          toast.success("Final audit submitted successfully.");
          refetch();
          refetchAudit();
          setSubmitToAuditor(false);
          setSubmitting(false);
        }, 2000);
      }
    } catch (error) {
      toast.error("Failed to submit activities. Please try again.");
      console.error("Error submitting activities:", error);
      setSubmitToAuditor(false);
      setSubmitting(false);
    }
  }

  async function onSubmitToAuditorHandler(e: any) {
    preventDefault(e);
    const todoIds = selectedRows.map((x) => x.id);
    const newAuditStatus = getNewAuditStatusAfterSubmit(currentAudit.status);
    if (newAuditStatus !== currentAudit.status) {
      if (newAuditStatus === LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_SUBMITTED) {
        await submitToAuditorPreliminaryAudit(todoIds);
      } else if (newAuditStatus === LOCATION_AUDIT_STATUS.FINAL_AUDIT_SUBMITTED) {
        await submitToAuditorFinalAudit(todoIds);
      }
    }
  }

  async function downloadReport(event: any, reportType: "preliminary" | "final") {
    preventDefault(event);
    if (currentAudit && currentAudit.id) {
      if (
        reportType === "preliminary" &&
        [LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_PENDING, LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_SUBMITTED].includes(
          currentAudit.status
        )
      ) {
        toast.warn("Preliminary report is not available as Preliminary audit is not yet published.");
        return;
      }
      if (
        reportType === "final" &&
        [
          LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_PENDING,
          LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_SUBMITTED,
          LOCATION_AUDIT_STATUS.FINAL_AUDIT_PENDING,
          LOCATION_AUDIT_STATUS.FINAL_AUDIT_SUBMITTED,
          LOCATION_AUDIT_STATUS.FINAL_AUDIT_EDITING,
        ].includes(currentAudit.status)
      ) {
        toast.warn("Final report is not available as Final audit is not yet published.");
        return;
      }
      setSubmitting(true);
      try {
        let reportData: any;
        if (currentAudit && (currentAudit.preliminary_report || currentAudit.final_report)) {
          reportData = reportType === "preliminary" ? currentAudit.preliminary_report : currentAudit.final_report;
        } else {
          const response = await api.get(`/api/v1/location_audits/${currentAudit.id}`, {
            include: AUDIT_INCLUDE,
            includes: AUDIT_INCLUDE,
          });
          reportData =
            reportType === "preliminary" ? response.data.preliminary_report : response.data.final_report;
        }
        setSelectedReportForDownload(reportData);
        const { s3_region, s3_key, s3_bucket } = reportData || {};
        if (s3_region && s3_key && s3_bucket) {
          auditReport({ s3_region, s3_key, s3_bucket });
        } else {
          const reportTypeName = reportType.charAt(0).toUpperCase() + reportType.slice(1);
          toast.warn(`${reportTypeName} report is not available`);
        }
      } catch (error) {
        console.error("Error fetching audit:", error);
      }
      setSubmitting(false);
    }
  }

  function handleExport() {
    if (total < 1) {
      toast.warn("There are no records available for this filter criteria.");
      return;
    }
    const exportPayload: any = {
      page: 1,
      per: total,
      sorts: ["month desc"],
      filters: (payload && payload.filters) ? { ...payload.filters } : {},
      include: {
        act: [],
        rule: [],
        activity: [],
        location: [],
        associate_company: [],
        act_state_mapping: {
          include: ["rule_compliance_detail"],
        },
      },
    };
    exportTodos(exportPayload);
  }

  function downloadForm(row: any) {
    setSubmitting(true);
    api
      .get(`/api/v1/act_state_mappings/${row.act_state_mapping_id}`)
      .then((response) => {
        if (response.data.file_path) {
          download(response.data.file_name, response.data.file_path);
        } else {
          toast.warn("No files available");
        }
      })
      .finally(() => setSubmitting(false));
  }

  function bulkFileDownloadthroughZip() {
    const selectedStatuses = Object.keys(checkedStatuses).filter(
      (status) => checkedStatuses[status] === true
    );
    const filters = {
      location_audit_id_eq: currentAudit.id,
      status_in: selectedStatuses,
    };
    const todoIds = (activities || []).filter((t: any) => t.id).map((t: any) => t.id);
    if (todoIds.length > 0) {
      api
        .post(
          "/api/v1/todo_details/bulk_download",
          { filters, todo_ids: todoIds },
          { responseType: "arraybuffer", headers: { "Content-Type": "application/zip" } }
        )
        .then((response: any) => {
          if (response instanceof AxiosError) {
            const contentType = (response.response && response.response.headers["content-type"]) || "";
            if (contentType.includes("application/json")) {
              const errorJson = (response.response || {}).data || {};
              toast.warning((errorJson || {}).error || [ERROR_MESSAGES.DEFAULT]);
            }
          } else {
            const respo = ((response || {}).data || { data: {} }).data;
            if (respo && respo.success) {
              const zipFileUrl = decodeURIComponent(respo.file_url);
              downloadWithPresignedUrl(
                `AuditActivities_${new Date().toISOString().split("T")[0]}.zip`,
                zipFileUrl
              );
              toast.success("Zip File Downloaded");
            } else {
              toast.warning(respo && respo.error);
            }
          }
        })
        .catch((error: any) => {
          if (error instanceof AxiosError) {
            const contentType = (error.response && error.response.headers["content-type"]) || "";
            if (contentType.includes("application/json")) {
              const errorJson = (error.response || {}).data || {};
              toast.warning((errorJson || {}).error || [ERROR_MESSAGES.DEFAULT]);
            }
          } else {
            console.error("Error downloading the file:", error);
            toast.warning("Failed to download the ZIP files.");
          }
        });
    } else {
      toast.warning("There are no files available.");
    }
  }

  function backToAuditList(e: any) {
    e.preventDefault();
    navigate("/location-audits", { state });
  }

  function handleVendorBulkUploadClose(refresh = false) {
    if (refresh) {
      refetch();
      queryClient.invalidateQueries(['activityDocuments']);
    }
    setVendorUpload(false);
  }

  function handleBulkUploadClose(refresh = false) {
    if (refresh) {
      refetch();
      queryClient.invalidateQueries(['activityDocuments']);
    }
    setBulkUpload(false);
  }

  // ─── Checkbox selection ───────────────────────────────────────────────────

  const checkedActivities = (activities || []).filter((a: any) => rowCheckState[a.id]);
  const allChecked =
    (activities || []).length > 0 && (activities || []).every((a: any) => rowCheckState[a.id]);

  useEffect(() => {
    setRowCheckState({});
  }, [activities]);

  // ─── Derived values ───────────────────────────────────────────────────────

  const totalPages = Math.ceil(total / perPage) || 1;
  const showingFrom = total ? (page - 1) * perPage + 1 : 0;
  const showingTo = Math.min(page * perPage, total);
  const auditStatusLabel =
    AUDIT_STATUS_LABELS[(currentAudit || {}).status as keyof typeof AUDIT_STATUS_LABELS] ||
    (currentAudit || {}).status || "";
  const locationName =
    (currentAudit && currentAudit.location && currentAudit.location.name) || "";
  const auditMonth = (currentAudit && currentAudit.month) || "";
  const auditYear = (currentAudit && currentAudit.year) || "";

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="activities-management-v2">

      {/* Back button */}
      <button className="al-back-btn" onClick={backToAuditList} type="button">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M15 18l-6-6 6-6" />
        </svg>
        Back to Location Audits
      </button>

      {/* Title bar */}
      <div className="al-title-bar">
        <div>
          <div className="al-title">
            Activity List:&nbsp;
            <span className="al-title-location">
              {locationName}{auditMonth && auditYear ? " - " + auditMonth + " (" + auditYear + ")" : ""}
            </span>
            <button
              type="button"
              className="al-icon-btn"
              onClick={() => setShowAuditDetails(true)}
              title="Audit Details"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2965ad" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            </button>
            {auditStatusLabel && (
              <span className={"al-audit-status-pill " + getAuditStatusPillClass((currentAudit || {}).status)}>
                {auditStatusLabel}
              </span>
            )}
          </div>
          {/* <div className="va-breadcrumb">Dashboard / Location Audits / Activity List</div> */}
        </div>
      </div>

      {/* Filter card */}
      <div className="al-filter-card">
        <div className="al-filters-row">
          {/* Due Date */}
          <div className="al-filter-group">
            <label>Due Date</label>
            <DatePicker
              className="al-datepicker-input"
              selected={inlineDueDate}
              dateFormat="dd-MM-yyyy"
              onChange={(date: Date | null) => setInlineDueDate(date)}
              placeholderText="dd-mm-yyyy"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              isClearable
              portalId="al-datepicker-portal"
            />
          </div>

          {/* Activity Type */}
          <div className="al-filter-group">
            <label>Activity Type</label>
            <Select
              placeholder="Activity Type"
              options={ActivityType.map((x: any) => ({ label: x, value: x }))}
              onChange={(selected) => setInlineActivityType((selected || []).map((s: any) => s.value))}
              value={inlineActivityType.map((t) => ({ value: t, label: t }))}
              className="al-rs"
              classNamePrefix="al-rs"
              isMulti={true}
              isClearable={true}
              closeMenuOnSelect={false}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </div>

          {/* Audit Type */}
          <div className="al-filter-group">
            <label>Audit Type</label>
            <AuditSelect
              options={[
                { value: '', label: 'All' },
                { value: ACTIVITY_TYPE.AUDIT, label: ACTIVITY_TYPE.AUDIT },
                { value: ACTIVITY_TYPE.NO_AUDIT, label: ACTIVITY_TYPE.NO_AUDIT },
                { value: ACTIVITY_TYPE.PHYSICAL_AUDIT, label: ACTIVITY_TYPE.PHYSICAL_AUDIT },
              ]}
              value={{ value: inlineAuditType, label: inlineAuditType === '' ? 'All' : inlineAuditType }}
              onChange={(opt: any) => setInlineAuditType(opt ? opt.value : '')}
              isClearable={false}
            />
          </div>

          {/* More Filters */}
          <div className="al-filter-group" style={{ alignSelf: "flex-end", flex: "none", minWidth: "auto", maxWidth: "none" }}>
            <button
              type="button"
              className="al-more-filters-btn"
              onClick={() => setShowMoreFilters(true)}
            >
              {/* <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="8" y1="12" x2="16" y2="12" />
                <line x1="12" y1="18" x2="12" y2="18" strokeLinecap="round" />
              </svg> */}
              More Filters
              {getMoreFiltersCount() > 0 && (
                <span className="al-mf-badge">+{getMoreFiltersCount()}</span>
              )}
            </button>
          </div>
        </div>

        {/* Status bar */}
        <div className="al-status-bar">
          <label>Evidence Status</label>
          {STATUS_BTNS.map((btn) => (
            <button
              key={btn.name}
              type="button"
              className={"al-status-btn al-status-btn-" + btn.name + (checkedStatuses[btn.name] ? " active" : "")}
              onClick={() =>
                setCheckedStatuses((prev: any) => ({
                  ...prev,
                  [btn.name]: !prev[btn.name],
                }))
              }
            >
              {btn.label}
            </button>
          ))}
          <div className="al-action-buttons">
            {/* Download Report */}
            {hasUserAccess(USER_PRIVILEGES.SUBMITTER_ACTIVITIES_DOWNLOAD_REPORT) && (
              <div ref={downloadWrapRef} className={"al-download-wrap" + (showDownloadMenu ? " open" : "")}>
                <button
                  type="button"
                  className="al-download-report-btn"
                  onClick={() => setShowDownloadMenu((v) => !v)}
                >
                  Download Report
                  <svg className="al-dl-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                <div className="al-download-popover">
                  <button type="button" className="al-download-option" onClick={() => { downloadReport(null, "preliminary"); setShowDownloadMenu(false); }}>
                    Preliminary Report
                  </button>
                  <button type="button" className="al-download-option" onClick={() => { downloadReport(null, "final"); setShowDownloadMenu(false); }}>
                    Final Report
                  </button>
                </div>
              </div>
            )}
            {/* Submit to Auditor */}
            {hasUserAccess(USER_PRIVILEGES.SUBMITTER_ACTIVITIES_SUBMIT) && userRole === USER_ROLE.VENDOR_ADMIN && (
              <button type="button" className="al-publish-btn" onClick={onSubmitToAuditor}>
                Submit to Auditor
              </button>
            )}
            {/* Three-dots menu */}
            <div ref={ellipsisWrapRef} className={"al-actions-menu-wrap" + (showEllipsis ? " open" : "")}>
              <button
                type="button"
                className="al-actions-menu-trigger"
                onClick={() => setShowEllipsis((v) => !v)}
                title="More options"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="5"  r="2.5" fill="#1E1E1E"/>
                  <circle cx="12" cy="12" r="2.5" fill="#1E1E1E"/>
                  <circle cx="12" cy="19" r="2.5" fill="#1E1E1E"/>
                </svg>
              </button>
              <div className={"al-actions-menu" + (showEllipsis ? " open" : "")}>
                <button type="button" onClick={() => { setShowEllipsis(false); handleExport(); }}>
                  Export
                </button>
                <button type="button" onClick={() => { bulkFileDownloadthroughZip(); setShowEllipsis(false); }}>
                  All Files Download
                </button>
                {checkedActivities.length > 0 && (
                  <button type="button" onClick={() => { setShowEllipsis(false); setVendorUpload(true); }}>
                    Bulk Upload
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table wrapper */}
      <div className="al-table-wrapper">

        {/* Scrollable table */}
        <div className="al-table-scroll" ref={tableScrollRef}>
          <table className="al-table">
            <thead>
              <tr>
                {userRole === 'Vendor User' && (
                  <th className="al-col-checkbox">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={(e) => {
                        const next: Record<string, boolean> = {};
                        (activities || []).forEach((a: any) => { next[a.id] = e.target.checked; });
                        setRowCheckState(next);
                      }}
                    />
                  </th>
                )}
                <th className="al-col-type" aria-label="Activity audit type"></th>
                <th className="al-col-act">Act</th>
                <th className="al-col-rule">Rule</th>
                <th className="al-col-activity">Activity</th>
                <th className="al-col-acttype">Activity Type</th>
                <th className="al-col-auditdate">Audit Due Date</th>
                <th className="al-col-submitdate">Vendor Submitted Date</th>
              </tr>
            </thead>
            <tbody ref={tbodyRef}>
              {(activities || []).map((row: any) => {
                const act = (row && row.act) || {};
                const rule = (row && row.rule) || {};
                const activityObj = (row && row.activity) || {};
                const auditStatus = (row && row.audit_status) || "";
                return (
                  <tr
                    key={row && row.id}
                    className={row && row.published ? "activity-published" : ""}
                  >
                    {userRole === 'Vendor User' && (
                      <td className="al-col-checkbox" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={!!rowCheckState[row && row.id]}
                          onChange={(e) =>
                            setRowCheckState((prev) => ({ ...prev, [row.id]: e.target.checked }))
                          }
                        />
                      </td>
                    )}
                    <td className="al-col-type">
                      <span className="al-type-icon" title={row.auditted || ""}>
                        {getActivityTypeIcon(row.auditted || "")}
                      </span>
                    </td>
                    <TextCell className="al-col-act" value={act.name || ""} />
                    <TextCell className="al-col-rule" value={rule.name || ""} />
                    <TextCell className="al-col-activity" value={activityObj.name || ""} />
                    <TextCell className="al-col-acttype" value={activityObj.activity_type || ""} />
                    <TextCell className="al-col-auditdate" value={(row && row.due_date) ? dayjs(row.due_date).format("DD-MM-YYYY") : ""} />
                    <TextCell className="al-col-submitdate" value={(row && row.submitted_date) ? dayjs(row.submitted_date).format("DD-MM-YYYY") : "—"} />
                  </tr>
                );
              })}
              {isFetching && (
                <tr>
                  <td colSpan={userRole === 'Vendor User' ? 8 : 7} style={{ textAlign: "center", padding: 32, color: "#6b7280" }}>
                    Loading...
                  </td>
                </tr>
              )}
              {!isFetching && (!activities || activities.length === 0) && (
                <tr>
                  <td colSpan={userRole === 'Vendor User' ? 8 : 7} style={{ textAlign: "center", padding: 32, color: "#6b7280" }}>
                    No activities found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Sticky panel */}
        <div className="al-sticky-panel">
          <div className="al-sticky-header">
            <div className="al-h-compliance">Compliance</div>
            <div className="al-h-status">Status</div>
            <div className="al-h-actions">Actions</div>
          </div>
          <div className="al-sticky-body" ref={stickyBodyRef}>
            <div className="al-sticky-body-inner" ref={stickyBodyInnerRef}>
              {(activities || []).map((row: any) => {
                const status = (row && row.status) || "";
                const auditStatus = (row && row.audit_status) || "";
                return (
                  <div className={"al-sticky-row" + (row && row.published ? " activity-published" : "")} key={row && row.id}>
                    <div className="al-sticky-cell-compliance">
                      <InfoTooltip html={(row && row.audit_remarks) || ""} />
                      <span className={
                        "al-comp-pill " +
                        (auditStatus === AUDIT_STATUS.COMPLIANT ? "comp-yes"
                          : auditStatus === AUDIT_STATUS.NON_COMPLIANCE ? "comp-no"
                          : "comp-na")
                      }>
                        {STATUS_MAPPING[auditStatus] || auditStatus || "—"}
                      </span>
                    </div>
                    <div className="al-sticky-cell-status">
                      <InfoTooltip html={(row && row.forms_status_remarks) || ""} />
                      <span className={"al-status-pill al-status-pill-" + status}>
                        {STATUS_MAPPING[status] || status}
                      </span>
                    </div>
                    <div className="al-sticky-cell-actions">
                      <button
                        type="button"
                        className="al-icon-btn"
                        title={(currentAudit || {}).status === LOCATION_AUDIT_STATUS.DROPPED_AUDIT || !hasUserAccess(USER_PRIVILEGES.SUBMITTER_ACTIVITIES_UPLOAD) ? "View" : "Edit"}
                        onClick={() => setActivity(row)}
                      >
                        {(currentAudit || {}).status !== LOCATION_AUDIT_STATUS.DROPPED_AUDIT && hasUserAccess(USER_PRIVILEGES.SUBMITTER_ACTIVITIES_UPLOAD) ? (
                          <svg width="16" height="16" viewBox="-0.75 -0.75 15 15" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M6 1.414L1.333 1.414C0.98 1.414 0.641 1.555 0.391 1.805C0.14 2.055 0 2.394 0 2.748L0 12.081C0 12.435 0.14 12.774 0.391 13.024C0.641 13.274 0.98 13.414 1.333 13.414L10.667 13.414C11.02 13.414 11.359 13.274 11.609 13.024C11.86 12.774 12 12.435 12 12.081L12 7.414" />
                            <path d="M11 0.414C11.265 0.149 11.625 0 12 0C12.375 0 12.735 0.149 13 0.414C13.265 0.679 13.414 1.039 13.414 1.414C13.414 1.789 13.265 2.149 13 2.414L6.667 8.748L4 9.414L4.667 6.748L11 0.414Z" />
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        )}
                      </button>
                      <button
                        type="button"
                        className="al-icon-btn"
                        title="Download"
                        onClick={() => downloadForm(row)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
              {(!activities || activities.length === 0) && (
                <div className="al-sticky-row" style={{ height: 72, justifyContent: "center", color: "#6b7280" }}>
                  &nbsp;
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Pagination */}
        <div className="al-pagination">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, color: '#757575', fontFamily: 'Montserrat', fontWeight: 500, whiteSpace: 'nowrap' }}>
                Results per page
              </span>
              <select
                value={perPage}
                onChange={(e: any) => handlePerPageChange(Number(e.target.value))}
                style={{ fontSize: 12, fontWeight: 600, color: '#1e1e1e', fontFamily: 'Montserrat', border: '1px solid #d1d5db', borderRadius: 6, padding: '3px 4px', cursor: 'pointer', background: '#fff', outline: 'none' }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
            <span>
              {total > 0
                ? "Showing " + showingFrom + " to " + showingTo + " of " + total + " results"
                : "No records"}
            </span>
          </div>
          <div className="al-pagination-controls">
            <button
              type="button"
              className="pagination-btn"
              disabled={page <= 1}
              onClick={() => handlePageNav(page - 1)}
            >
              &lsaquo; Prev
            </button>
            {getPageNumbers(page, totalPages).map((p) => (
              <button
                key={p}
                type="button"
                className={"pagination-btn" + (p === page ? " active" : "")}
                onClick={() => handlePageNav(p)}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              className="pagination-btn"
              disabled={page >= totalPages}
              onClick={() => handlePageNav(page + 1)}
            >
              Next &rsaquo;
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showMoreFilters && (
        <AuditLocationFiltersV2
          onApply={handleMoreFiltersApply}
          onClose={() => setShowMoreFilters(false)}
          initialFilters={appliedMoreFilters}
        />
      )}

      {showAuditDetails && (
        <AuditDetailsModalV2
          onClose={() => setShowAuditDetails(false)}
          audit={currentAudit}
          auditType="location"
        />
      )}

      {submitToAuditor && (
        <SubmitToAuditorModalV2
          onClose={() => setSubmitToAuditor(false)}
          onSubmit={onSubmitToAuditorHandler}
          selectedRows={selectedRows}
          auditStatus={(currentAudit || {}).status}
        />
      )}

      {!!activity && (
        <EditActivityModalV2
          activity={activity}
          onClose={() => setActivity(null)}
          onSubmit={refetch}
          selectedItems={selectedRows}
          auditStatus={(currentAudit || {}).status}
        />
      )}

      {vendorUpload && (
        <VendorBulkUploadModal
          onClose={handleVendorBulkUploadClose}
          selectedItems={checkedActivities}
          isVendor={false}
        />
      )}

      {bulkUpload && (
        <BulkUploadModal onClose={handleBulkUploadClose} onSubmit={refetch} />
      )}

      {!!alertMessage && (
        <AlertModalV2
          message={alertMessage}
          onClose={() => {
            setAlertMessage(null);
          }}
        />
      )}

      {(submitting || exporting || exportingTodos) && <PageLoader />}
    </div>
  );
}

export default ActivitiesManagementV2;
